/**
 * Inventory API client (T71). Wires the T51 dispensing/stock endpoint so the
 * Stock page (and any low-stock indicator elsewhere) can read tenant-scoped
 * batch-level on-hand quantities.
 */
import { apiClient } from "./client";

export interface StockRow {
  medicineId: string;
  medicineName: string;
  batchId: string;
  lotNumber: string;
  expiryDate: string;
  quantityOnHand: number;
}

/** A page of `stockSearch` results. `hasMore` false = stop paginating (#18604). */
export interface StockPage {
  items: StockRow[];
  hasMore: boolean;
}

export interface DispenseLine {
  itemId: string;
  medicineId: string;
  requested: number;
  dispensed: number;
}
export interface DispenseResult {
  prescriptionId: string;
  lines: DispenseLine[];
}
export interface DispenseEvent {
  id: string;
  medicineId: string;
  medicineName: string;
  batchId: string;
  quantity: number;
  dispensedAt: string | null;
}

export const inventoryApi = {
  stock(): Promise<StockRow[]> {
    return apiClient.get<StockRow[]>("/v1/dispensing/stock").then((r) => r.data);
  },
  /**
   * One page of lots matching `q` — backs the "Chọn lô" combobox (#18604). Kept separate from
   * `stock()` above, which loads everything for the Stock screen.
   */
  stockSearch(params: {
    q: string;
    page: number;
    size?: number;
  }): Promise<StockPage> {
    return apiClient
      .get<StockPage>("/v1/dispensing/stock/search", {
        params: { q: params.q, page: params.page, size: params.size ?? 20 },
      })
      .then((r) => r.data);
  },
  /** Cấp phát đơn thuốc (trừ tồn kho FEFO). */
  dispense(prescriptionId: string): Promise<DispenseResult> {
    return apiClient
      .post<DispenseResult>("/v1/dispensing", { prescriptionId })
      .then((r) => r.data);
  },
  dispenseEvents(prescriptionId: string): Promise<DispenseEvent[]> {
    return apiClient
      .get<DispenseEvent[]>("/v1/dispensing", { params: { prescriptionId } })
      .then((r) => r.data);
  },
};

export const inventoryQueryKeys = {
  stock: () => ["inventory", "stock"] as const,
  stockSearch: (q: string) => ["inventory", "stock-search", q] as const,
  dispenseEvents: (prescriptionId: string) =>
    ["inventory", "dispense-events", prescriptionId] as const,
};
